// Контракт API пульта (план M, M2 = упрощённая C2-10): люди размечают вердикты судьи, код считает, насколько судья прав.
//
// - `POST /api/v1/eval/judges/labels` (admin и editor) `{ turnId, attempt, judge, label, note? }` → `{ label }`: метка
//   человека по одному признаку вердикта судьи у попытки хода; повтор той же тройки (признак, попытка хода, сотрудник)
//   перезаписывает метку;
// - `GET /api/v1/eval/judges/calibration` (любой вошедший) → `JudgeCalibration`: по каждому признаку — сколько меток, TPR,
//   TNR, доля совпадений и статус за всё время и за последние 7 суток МСК; считается на лету из меток и `turns.checks`,
//   без вызовов модели;
// - в разборе диалога у хода — `judge`: попытки, где судья вынес вердикт, и по каждому признаку что он сказал и моя метка.
//
// Слова:
// - `verdict` — что сказал судья по признаку: `true` — по этому признаку ответ хороший;
// - `label` — истина человека в том же виде: `true` — по этому признаку ответ хороший;
// - «Судья прав» — метка совпала с вердиктом. Провал — метка `false`; TPR — доля провалов, которые судья признал провалом;
//   TNR — доля хороших ответов, которые судья пропустил; совпадение — доля меток, где судья прав.
// - попытки считаются с 1, как в `turns.checks` по порядку; образец метки в базе — `turn:<id хода>:<попытка>`.
//
// Правило контракта: файл импортирует только `zod` и соседей (`./*.js`), типы — `z.infer`.
import { z } from 'zod';
import { MomentSchema } from './common.js';

// ───────────────────────────── признаки ─────────────────────────────

/**
 * Признаки вердикта судьи, которые размечают люди: по одному на поле «да / нет» вердикта (`JudgeVerdict`, C1-12). Имена —
 * оценщиков C2-10 с приставкой `prod_` (это боевой судья хода, а не рубрика прогонов).
 */
export const JUDGE_ASPECT_IDS = ['prod_answered_question', 'prod_no_unsupported_claims', 'prod_not_template'] as const;
export const JudgeAspectSchema = z.enum(JUDGE_ASPECT_IDS);
export type JudgeAspect = z.infer<typeof JudgeAspectSchema>;

/** Признак словами: «хороший ответ по этому признаку» — строки таблицы калибровки. */
export const JUDGE_ASPECT_RU: Readonly<Record<JudgeAspect, string>> = {
  prod_answered_question: 'Ответила на вопрос клиента',
  prod_no_unsupported_claims: 'Нет утверждений без опоры',
  prod_not_template: 'Не звучит шаблоном',
};

/** Что сказал судья по признаку — строка у попытки в разборе. */
export const JUDGE_VERDICT_RU: Readonly<Record<JudgeAspect, { readonly good: string; readonly bad: string }>> = {
  prod_answered_question: { good: 'Судья: ответ отвечает на вопрос клиента', bad: 'Судья: ответ не отвечает на вопрос клиента' },
  prod_no_unsupported_claims: { good: 'Судья: утверждений без опоры нет', bad: 'Судья: в ответе есть утверждения без опоры' },
  prod_not_template: { good: 'Судья: ответ не звучит шаблоном', bad: 'Судья: ответ звучит шаблоном' },
};

/** Вопросов в сообщениях клиента не было: пропустить нечего, судья засчитывает признак как «хорошо». */
export const JUDGE_NO_QUESTION_RU = 'Судья: вопроса в сообщениях клиента не было';

/** Метка человека из вердикта судьи и выбора «Судья прав» / «Судья ошибся». */
export function humanLabelOf(verdict: boolean, judgeRight: boolean): boolean {
  return judgeRight ? verdict : !verdict;
}

/** Прав ли судья: его вердикт совпал с меткой человека. */
export function judgeRightOf(verdict: boolean, label: boolean): boolean {
  return verdict === label;
}

// ───────────────────────────── вердикты в разборе ─────────────────────────────

/** Сколько знаков в заметке к метке: от 0 до этого. */
export const JUDGE_LABEL_NOTE_MAX = 300;

/** Моя метка по признаку: прав ли судья и заметка. */
export const JudgeMineSchema = z.object({ judgeRight: z.boolean(), note: z.string().nullable() });
export type JudgeMine = z.infer<typeof JudgeMineSchema>;

/** Признак у попытки: что сказал судья (с цитатами, если они есть) и моя метка (`null` — не размечал или не может). */
export const JudgeAspectViewSchema = z.object({
  judge: JudgeAspectSchema,
  verdict: z.boolean(),
  verdictRu: z.string().min(1),
  /** «Вопрос без ответа: «…»» или «Без опоры: «…»; «…»»; нечего показать — `null`. */
  detailRu: z.string().nullable(),
  mine: JudgeMineSchema.nullable(),
});
export type JudgeAspectView = z.infer<typeof JudgeAspectViewSchema>;

/** Попытка хода, где судья вынес вердикт: номер с 1, текст варианта и признаки по порядку `JUDGE_ASPECT_IDS`. */
export const JudgeAttemptViewSchema = z.object({
  attempt: z.number().int().positive(),
  parts: z.array(z.string()),
  aspects: z.array(JudgeAspectViewSchema).min(1),
});
export type JudgeAttemptView = z.infer<typeof JudgeAttemptViewSchema>;

// ───────────────────────────── метка ─────────────────────────────

/** Тело `POST /eval/judges/labels`: правила проверяет сервер (тексты ниже). */
export const JudgeLabelBodySchema = z.object({
  turnId: z.string().min(1),
  attempt: z.number().int().positive(),
  judge: JudgeAspectSchema,
  label: z.boolean(),
  note: z.string().optional(),
});
export type JudgeLabelBody = z.infer<typeof JudgeLabelBodySchema>;

/** Записанная метка: вердикт судьи и прав ли он — для пульта; `at` — момент этой метки. */
export const JudgeLabelViewSchema = z.object({
  turnId: z.string().min(1),
  attempt: z.number().int().positive(),
  judge: JudgeAspectSchema,
  label: z.boolean(),
  verdict: z.boolean(),
  judgeRight: z.boolean(),
  note: z.string().nullable(),
  at: MomentSchema,
});
export type JudgeLabelView = z.infer<typeof JudgeLabelViewSchema>;

export const JudgeLabelResponseSchema = z.object({ label: JudgeLabelViewSchema });
export type JudgeLabelResponse = z.infer<typeof JudgeLabelResponseSchema>;

/** Тексты отказов метки. */
export const JUDGE_LABEL_ERRORS_RU = {
  bodyShape: 'Тело запроса — JSON вида {"turnId": "…", "attempt": 1, "judge": "prod_answered_question", "label": true, "note": "…"}.',
  badTurn: 'turnId — id хода из разбора диалога.',
  badAttempt: 'attempt — номер попытки хода: целое число от 1.',
  unknownJudge: 'Нет такого признака судьи',
  badLabel: 'label — true (по этому признаку ответ хороший) или false (плохой).',
  badNote: 'note — текст заметки.',
  noteTooLong: `Заметка — не больше ${String(JUDGE_LABEL_NOTE_MAX)} знаков`,
  turnNotFound: 'Ход не найден',
  /** «У этого хода нет попытки 3». */
  noAttempt: (attempt: number): string => `У этого хода нет попытки ${String(attempt)}`,
} as const;

// ───────────────────────────── калибровка ─────────────────────────────

/** Сколько меток нужно, чтобы считать: всего и в каждом классе (провалы и хорошие ответы). */
export const CALIBRATION_MIN_LABELS = 30;
export const CALIBRATION_MIN_PER_CLASS = 10;
/** Судья сверен с людьми: TPR и TNR не ниже этого. */
export const CALIBRATION_TARGET = 0.85;

/** Сколько суток МСК в коротком окне калибровки (сегодня и шесть прошлых). */
export const CALIBRATION_WEEK_DAYS = 7;

export const CALIBRATION_STATUSES = ['calibrated', 'not_calibrated', 'insufficient'] as const;
export const CalibrationStatusSchema = z.enum(CALIBRATION_STATUSES);
export type CalibrationStatus = z.infer<typeof CalibrationStatusSchema>;

export const CALIBRATION_STATUS_RU: Readonly<Record<CalibrationStatus, string>> = {
  calibrated: 'сверен',
  not_calibrated: 'не сверен',
  insufficient: 'мало меток',
};

const ShareSchema = z.number().min(0).max(1).nullable();

/** Счёт признака за период: метки, доли (`null` — не из чего считать), статус и момент последней метки периода. */
export const JudgeScoreSchema = z.object({
  n: z.number().int().nonnegative(),
  /** Меток «плохо» (провалов). */
  failures: z.number().int().nonnegative(),
  /** Меток «хорошо». */
  goods: z.number().int().nonnegative(),
  tpr: ShareSchema,
  tnr: ShareSchema,
  agreement: ShareSchema,
  status: CalibrationStatusSchema,
  statusRu: z.string().min(1),
  lastLabelAt: MomentSchema.nullable(),
});
export type JudgeScoreView = z.infer<typeof JudgeScoreSchema>;

export const JudgeCalibrationRowSchema = z.object({
  judge: JudgeAspectSchema,
  judgeRu: z.string().min(1),
  allTime: JudgeScoreSchema,
  week: JudgeScoreSchema,
});
export type JudgeCalibrationRow = z.infer<typeof JudgeCalibrationRowSchema>;

/** Ответ `GET /eval/judges/calibration`: строки по `JUDGE_ASPECT_IDS`, начало окна «7 суток МСК» и момент расчёта. */
export const JudgeCalibrationSchema = z.object({
  rows: z.array(JudgeCalibrationRowSchema),
  weekFrom: MomentSchema,
  at: MomentSchema,
});
export type JudgeCalibration = z.infer<typeof JudgeCalibrationSchema>;

// ───────────────────────────── коды ошибок ─────────────────────────────

/**
 * Коды ошибок разметки судьи и их HTTP-статусы (копия `API_ERROR_STATUS`, `src/console/errors.ts`; совпадение держит
 * `tests/console/eval-judges.test.ts`).
 */
export const EVAL_JUDGES_ERROR_STATUS = {
  /** Тело не того вида, неизвестный признак, длинная заметка. */
  bad_request: 400,
  /** Хода или попытки нет. */
  not_found: 404,
  /** Судья эту попытку не оценивал — размечать нечего. */
  no_judge_verdict: 409,
} as const;
export type EvalJudgesErrorCode = keyof typeof EVAL_JUDGES_ERROR_STATUS;

/** Постоянные тексты этих ошибок (копия `API_ERRORS_RU`). */
export const EVAL_JUDGES_FIXED_ERRORS_RU = {
  no_judge_verdict: 'Судья эту попытку не оценивал: её не пропустила проверка кода или судья был выключен — размечать нечего',
} as const;
